import React from 'react';
import { useIntl } from 'react-intl';

import { CheckboxGroup } from '../../components/CheckboxGroup/CheckboxGroup';
import { DropdownBody } from '../../components/Dropdown/subcomponents/DropdownBody';
import { DropdownFooter } from '../../components/Dropdown/subcomponents/DropdownFooter';
import { useDropdown } from '../../components/hooks/useDropdown';
import { IconButton } from '../../components/IconButton/IconButton';
import { Layout } from '../../components/Layout/Layout';
import { RadioGroup } from '../../components/RadioGroup/RadioGroup';
import { Select } from '../../components/Select/Select';
import { Text } from '../../components/Text/Text';
import { Nullable } from '../../types/Nullable';
import {
  CommentFilters,
  CommentFiltersFlag,
  CommentFiltersOrder,
  CommentFiltersSource,
  CommentFiltersStatus,
  EnabledFilters,
} from './types';
import { testIsFiltersUpdated } from './utils';

interface Props {
  filters: CommentFilters;
  enabledFilters: EnabledFilters;
  onUpdateFilters: (updatedFilters: CommentFilters) => void;
}

export const CommentsFilters = ({
  filters,
  enabledFilters,
  onUpdateFilters,
}: Props) => {
  const intl = useIntl();

  const COMMENT_FILTERS_STATUS_OPTIONS = [
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.statusAll',
        defaultMessage: 'All',
        description: 'Status filter option: all',
      }),
      value: 'all',
    },
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.statusOpen',
        defaultMessage: 'Open Comments',
        description: 'Status filter option: open comments',
      }),
      value: 'open',
    },
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.statusResolved',
        defaultMessage: 'Resolved Comments',
        description: 'Status filter option: resolved comments',
      }),
      value: 'isResolved',
    },
  ];

  const COMMENT_FILTERS_SOURCE_OPTIONS = [
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.sourceAll',
        defaultMessage: 'All',
        description: 'Source filter option: all',
      }),
      value: 'all',
    },
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.sourceDocument',
        defaultMessage: 'In document comments',
        description: 'Source filter option: in document comments',
      }),
      value: 'document',
    },
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.sourceTicket',
        defaultMessage: 'Ticket comments',
        description: 'Source filter option: ticket comments',
      }),
      value: 'ticket',
    },
  ];

  const COMMENT_FILTERS_FLAG_OPTIONS = [
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.flagMentionMe',
        defaultMessage: 'Comments mention me',
        description: 'Flag filter option: comments mention me',
      }),
      value: 'mentionMe',
    },
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.flagByMe',
        defaultMessage: 'Comments by me',
        description: 'Flag filter option: comments by me',
      }),
      value: 'byMe',
    },
  ];

  const COMMENT_FILTERS_ORDER_OPTIONS = [
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.orderChronological',
        defaultMessage: 'Oldest to newest',
        description: 'Order filter option: oldest to newest',
      }),
      value: 'chronological' as CommentFiltersOrder,
    },
    {
      label: intl.formatMessage({
        id: 'CommentsFilters.orderDocumentPosition',
        defaultMessage: 'Position in document',
        description: 'Order filter option: position in document',
      }),
      value: 'documentPosition' as CommentFiltersOrder,
    },
  ];

  const CHECKBOX_CUSTOM_LABEL = intl.formatMessage({
    id: 'CommentsFilters.checkboxLabel',
    defaultMessage: 'Filter by:',
    description: 'Label for filter by checkbox group',
  });

  const isFiltersUpdated = !testIsFiltersUpdated(filters);
  const [wrap, _show, _hide, toggle] = useDropdown({
    children: (
      <DropdownBody>
        <Layout direction="column" spacing={4}>
          <Text variant="body-medium">
            {intl.formatMessage({
              id: 'CommentsFilters.status',
              defaultMessage: 'Status',
              description: 'Label for the status filter',
            })}
          </Text>
          <Select
            disabled={!enabledFilters.status}
            enablePortal={true}
            enableSorting={false}
            enableValueContainer={true}
            isClearable={false}
            isMulti={false}
            isSearchable={false}
            name="Status"
            options={COMMENT_FILTERS_STATUS_OPTIONS}
            placeholder={intl.formatMessage({
              id: 'CommentsFilters.selectStatus',
              defaultMessage: 'Select a status',
              description: 'Placeholder for status select',
            })}
            value={filters.status}
            onChange={(updatedValue) => {
              const updatedFilters = {
                ...filters,
                status: updatedValue as CommentFiltersStatus,
              };
              onUpdateFilters(updatedFilters);
            }}
          />

          <Text variant="body-medium">
            {intl.formatMessage({
              id: 'CommentsFilters.source',
              defaultMessage: 'Source',
              description: 'Label for the source filter',
            })}
          </Text>
          <Select
            disabled={!enabledFilters.source}
            enablePortal={true}
            enableSorting={false}
            enableValueContainer={true}
            isClearable={false}
            isMulti={false}
            isSearchable={false}
            name="Source"
            options={COMMENT_FILTERS_SOURCE_OPTIONS}
            placeholder={intl.formatMessage({
              id: 'CommentsFilters.selectSource',
              defaultMessage: 'Select a source',
              description: 'Placeholder for source select',
            })}
            value={filters.source}
            onChange={(updatedValue) => {
              const updatedFilters = {
                ...filters,
                source: updatedValue as CommentFiltersSource,
              };
              onUpdateFilters(updatedFilters);
            }}
          />

          <Text variant="body-medium">
            {intl.formatMessage({
              id: 'CommentsFilters.viewCommentsBy',
              defaultMessage: 'View comments by',
              description: 'Label for the view comments by filter',
            })}
          </Text>
          <RadioGroup
            disabled={!enabledFilters.order}
            name="view comments by"
            options={COMMENT_FILTERS_ORDER_OPTIONS}
            value={filters.order || null}
            onChange={(updatedValue: Nullable<CommentFiltersOrder>) => {
              const sourceValue =
                updatedValue === 'documentPosition'
                  ? 'document'
                  : filters.source;
              const updatedFilters = updatedValue
                ? { ...filters, order: updatedValue, source: sourceValue }
                : filters;
              onUpdateFilters(updatedFilters);
            }}
          />

          <Text variant="body-medium">{CHECKBOX_CUSTOM_LABEL}</Text>
          <CheckboxGroup
            disabled={!enabledFilters.flags}
            name={CHECKBOX_CUSTOM_LABEL}
            value={filters.flags}
            onChange={(values) => {
              const flags = values || [];
              const updatedFilters = {
                ...filters,
                flags: flags as CommentFiltersFlag[],
              };
              onUpdateFilters(updatedFilters);
            }}
            options={COMMENT_FILTERS_FLAG_OPTIONS}
          />
        </Layout>
      </DropdownBody>
    ),
    footer: (
      <DropdownFooter
        leftAction={{
          disabled: !isFiltersUpdated,
          text: intl.formatMessage({
            id: 'CommentsFilters.reset',
            defaultMessage: 'Reset Filters',
            description: 'Reset filters action',
          }),
          onClick: () =>
            onUpdateFilters({ status: 'open', flags: [], source: 'all' }),
        }}
      />
    ),
  });

  return wrap(
    <IconButton
      icon="filter"
      status={isFiltersUpdated ? 'active' : undefined}
      tooltip={intl.formatMessage({
        id: 'CommentsFilters.filterTooltip',
        defaultMessage: 'Set filters',
        description: 'Tooltip for the filter icon',
      })}
      onClick={toggle}
    />,
  );
};
